import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { inject, provideAppInitializer } from '@angular/core';
import { AppConfigService } from '@app/core/services/config.service';
import { ICache, authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { environment } from 'environments/environment';
import { EMPTY, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { brandIdInterceptor } from '../interceptors/brand-id.interceptor';
import { clientInfoInterceptor } from '../interceptors/client-info.interceptor';
import { cultureCodeInterceptor } from '../interceptors/culture-code.interceptor';
import { ocpApimSubscriptionInterceptor } from '../interceptors/ocp-apim-subscription.interceptor';
import { unrecoverableErrorInterceptor } from '../interceptors/unrecoverable-error.interceptor';
import { connectivityInterceptor } from '../interceptors/connectivity.interceptor';
import { ConnectivityService } from '../services/connectivity.service';
import { forcedUpdateInterceptor } from '../interceptors/forced-update.interceptor';
import { RecaptchaService } from '../services/recaptcha.service';
import { DataType, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { TranslationSandbox } from '../sandboxes/translation.sandbox';
import { impersonationInterceptor } from '@app/impersonation/impersonation.interceptor';
import { externalIdCacheInterceptor } from '../interceptors/external-id-cache.interceptor';

const secureCacheLocation = {
  get: function (key: string) {
    // Note: update from @auth0/auth0-spa-js v2.11.0 to v2.18.x will throw a missing refresh token here
    return SecureStorage.get(key);
  },
  set: function (key: string, value: DataType) {
    SecureStorage.set(key, value);
  },
  remove: function (key: string) {
    SecureStorage.remove(key);
  },
  allKeys: function () {
    return SecureStorage.keys();
  }
} as ICache;

const appInitializerFn = (
  configService: AppConfigService,
  recaptchaService: RecaptchaService,
  translationSandbox: TranslationSandbox,
  connectivityService: ConnectivityService
) => {
  return () => {
    return from(connectivityService.init().then(() => configService.loadConfigClient())).pipe(
      switchMap((config$) => config$),
      switchMap((config) => {
        recaptchaService.load(config.keys.googleRecaptchaSiteKey);
        if (config?.security?.auth0) {
          // Initialize Auth0 before loading translations so the auth interceptor
          // is configured when the translation HTTP request fires.
          configService.auth0InitializerFn({
            ...config?.security?.auth0,
            redirectUri: configService.callbackUri,
            cache: secureCacheLocation,
            securedRoutes: [],
            // update below
            allowedList: [
              `${environment.url.api}/identity/me/*`,
              `${environment.url.api}/membership/user/me`,
              `${environment.url.api}/membership/user/me/*`,
              `${environment.url.api}/user/me/*`,
              `${environment.url.api}/housing/*`,
              `${environment.url.api}/profile/me`,
              `${environment.url.api}/profile/me/*`,
              `${environment.url.api}/housing/*`,
              `${environment.url.api}/document`,
              `${environment.url.api}/document/*`,
              `${environment.url.api}/action/*`,
              {
                uri: `${environment.url.api}/config/*`,
                allowAnonymous: true,
              },
              `${environment.url.api}/notification/*`,
              `${environment.url.api}/employer/*`,
              `${environment.url.api}/work/me/`,
              `${environment.url.api}/work/me/*`,
              `${environment.url.api}/feedback/*`,
              `${environment.url.api}/file/*`,
              {
                uri: `${environment.url.api}/job/search`,
                allowAnonymous: true,
              },
              {
                uriMatcher: (uri): boolean => new RegExp(`${environment.url.api}/job/[^\/]*$`).test(uri),
                allowAnonymous: true,
              },
              {
                uriMatcher: (uri): boolean => new RegExp(`${environment.url.api}/job/.*/.*$`).test(uri),
                allowAnonymous: false,
              },
              `${environment.url.api}/job/offer*`,
              `${environment.url.api}/job/saved*`,
              `${environment.url.api}/job/applied*`,
              `${environment.url.api}/campaign/*`,
              `${environment.url.api}/calendar*`,
              `${environment.url.api}/task*`,
              `${environment.url.api}/inbox-message*`,
              `${environment.url.api}/availability*`,
              `${environment.url.api}/chat/*`,
              {
                uri: `${environment.url.api}/chatbot/*`,
                allowAnonymous: true,
              },
              `${environment.url.api}/timeregistration*`,
              {
                uri: `${environment.url.api}/common/*`,
                allowAnonymous: true,
              },
              `${environment.url.api}/hubs*`,
              `${environment.url.api}/invitation*`,
              `${environment.url.api}/onboarding*`,
              `${environment.url.api}/shift*`,
              `${environment.url.api}/loyalty*`,
              `${environment.url.api}/event-calendar*`,
              `${environment.url.api}/flow*`,
              `${environment.url.api}/booking*`,
              `${environment.url.api}/dashboard*`,
              `${environment.url.api}/branch*`,
              `${environment.url.api}/reporting*`,
              `${environment.url.api}/balance*`,
              `${environment.url.api}/celebration*`,
            ],
          });
        }
        return from(translationSandbox.init());
      }),
      catchError((error) => {
        console.error(error);
        //Catch the failed config fetch if device is offline
        //We must pass configuration to auth0, otherwise the app runs into an error and doesn't work
        //It would be great if this could be circumvented, conditional import of auth0?
        configService.auth0InitializerFn({
          audience: '',
          clientId: '',
          domain: '',
          securedRoutes: [],
          allowedList: [],
          redirectUri: '',
        });
        return of(EMPTY);
      })
    );
  };
};

export const APP_PROVIDERS = [
  provideAppInitializer(() => {
    const initializerFn = (appInitializerFn)(
      inject(AppConfigService),
      inject(RecaptchaService),
      inject(TranslationSandbox),
      inject(ConnectivityService)
    );
    return initializerFn();
  }),
  provideHttpClient(
    withInterceptors([
      connectivityInterceptor,
      authHttpInterceptorFn,
      brandIdInterceptor,
      clientInfoInterceptor,
      cultureCodeInterceptor,
      ocpApimSubscriptionInterceptor,
      unrecoverableErrorInterceptor,
      forcedUpdateInterceptor,
      impersonationInterceptor,
      externalIdCacheInterceptor,
    ])
  ),
  provideAuth0(),
];
